import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { apiPostForm, type ReconcileRequest, type ReconciliationResult } from "@/shared/api";

/**
 * Appends the seven optional Boot Barn wholesale fields, each only when it is
 * on / present.
 *
 * Every field is omitted rather than sent false/empty, so a run that never
 * touches the Boot Barn wholesale card posts the exact form it always did -
 * byte for byte the same request as before these fields existed. Split out of
 * {@link useReconcile} purely to keep that function's own complexity under the
 * lint budget; the two together are one rule, read as one unit.
 */
function appendBbWcFields(form: FormData, request: ReconcileRequest): void {
  if (request.bbWcRateFallback) {
    form.append("bb_wc_rate_fallback", "true");
  }
  if (request.bbWcRateAlways) {
    form.append("bb_wc_rate_always", "true");
  }
  if (request.bbWcLandingRate != null) {
    form.append("bb_wc_landing_rate", String(request.bbWcLandingRate));
  }
  if (request.bbWcRateBands?.length) {
    form.append("bb_wc_rate_bands", JSON.stringify(request.bbWcRateBands));
  }
  if (request.bbWcCustomsFallback) {
    form.append("bb_wc_customs_fallback", "true");
  }
  if (request.bbWcCustomsAlways) {
    form.append("bb_wc_customs_always", "true");
  }
  if (request.bbWcCustomsRate != null) {
    form.append("bb_wc_customs_rate", String(request.bbWcCustomsRate));
  }
}

/**
 * Runs a reconciliation.
 *
 * A mutation rather than a query: it is not idempotent from the user's point of
 * view, it is slow, and it should never fire on a re-render.
 */
export function useReconcile(): UseMutationResult<
  ReconciliationResult,
  Error,
  ReconcileRequest
> {
  return useMutation({
    mutationFn: async (request: ReconcileRequest) => {
      const form = new FormData();
      form.append("ops_master", request.opsMaster);
      form.append("wc_source", request.wcSource);

      // Omitted entirely for a NetSuite run rather than sent empty: an empty
      // file part arrives server-side as a zero-byte upload, which would be
      // rejected as an empty workbook instead of read as "there is no file".
      if (request.wcReport) {
        form.append("wc_report", request.wcReport);
      }

      form.append("landing_rate", String(request.landingRate));
      form.append("lookback_days", String(request.lookbackDays));

      // One JSON array rather than repeated fields: it keeps each band's three
      // values bound together instead of relying on three parallel lists
      // staying the same length. Omitted entirely when there are none, so a
      // flat run posts exactly the form it always did.
      if (request.landingRateBands?.length) {
        form.append("landing_rate_bands", JSON.stringify(request.landingRateBands));
      }

      appendBbWcFields(form, request);

      return apiPostForm<ReconciliationResult>("/reconcile", form);
    },
    // Parsing two workbooks is expensive and not idempotent in cost. A retry
    // on a genuine failure just burns another 120 second budget.
    retry: false,
  });
}
